<template>
  <div class="tree-select-dropdown-tree">
    <TreeSelectControlSearch
      class="tree-select-dropdown-tree__search"
      :value="searchText"
      @input="$emit('searchTextInput', $event)"
    />
    <div class="tree-select-dropdown-tree__items">
      <template v-if="!treeIsLoading">
        <TreeSelectDropdownNode
          v-for="node in rootNodes"
          :key="node.id"
          :readonly="readonly"
          :force-open="forceOpen"
          :flat-choices="filteredNodes"
          :node-id="node.id"
          :selected-items="selectedItems"
          :indent="14"
          :no-child-text-getter="noChildTextGetter"
          :is-leaf-node-predicate="isLeafNodePredicate"
          @triggerCheckbox="$emit('triggerCheckbox', $event)"
          @handle-click-navigation="$emit('handle-click-navigation', $event)"
        />
      </template>
      <template v-else>
        <SkeletonLoader
          v-for="i in 5"
          :key="i"
          :is-loading="true"
          width="100%"
          height="8px"
          class="tree-select-dropdown-tree__node-skeleton"
        />
      </template>
    </div>
  </div>
</template>

<script>
  import TreeSelectDropdownNode from 'components/Inputs/Select/TreeSelect/TreeSelectDropdownNode';
  import TreeSelectControlSearch from 'components/Inputs/Select/TreeSelect/TreeSelectControlSearch';
  import SkeletonLoader from 'components/common/SkeletonLoader';
  import _ from 'lodash';

  _.noConflict();

  export default {
    name: 'TreeSelectDropdownTree',
    components: {TreeSelectControlSearch, TreeSelectDropdownNode, SkeletonLoader},
    props: {
      flatChoices: {
        type: Object,
        default: Object
      },
      selectedItems: {
        type: Array,
        default: Array
      },
      searchText: {
        type: String,
        default: ''
      },
      treeIsLoading: {
        type: Boolean,
        default: false
      },
      readonly: {
        type: Boolean,
        default: false
      },
      noChildTextGetter: {
        type: Function,
        required: true
      },
      isLeafNodePredicate: {
        type: Function,
        default: undefined
      }
    },
    data () {
      return {
        forceOpen: false
      };
    },
    computed: {
      filteredNodes () {
        if (!this.searchText) {
          return this.flatChoices;
        }
        const textLowerCase = this.searchText.toLowerCase();
        const requiredNodesIds = new Set([]);
        const unfilteredNodeIds = Object.keys(this.flatChoices).filter(
          i => this.flatChoices[i].name.toLowerCase().includes(textLowerCase));

        // it finds nodes to route
        const addParentToRequiredNodes = (node) => {
          if (!node?.parentId) {
            return;
          }
          requiredNodesIds.add(node.parentId);
          addParentToRequiredNodes(this.flatChoices[node.parentId]);
        };

        // it finds nodes to leaves
        const addChildrenToRequiredNodes = (node) => {
          if (node.isProject) {
            requiredNodesIds.add(node.id);
            return;
          }
          const nodeChildren = Object.values(this.flatChoices).
            filter(n => n.parentId === node.id);

          nodeChildren.forEach(n => {
            requiredNodesIds.add(n.id);
            addChildrenToRequiredNodes(this.flatChoices[n.id]);
          });
        };

        unfilteredNodeIds.forEach(nodeId => {
          requiredNodesIds.add(nodeId);
          addParentToRequiredNodes(this.flatChoices[nodeId]);
          addChildrenToRequiredNodes(this.flatChoices[nodeId]);
        });

        return _.keyBy(
          Array.from(requiredNodesIds).map(id => this.flatChoices[id]),
          'id'
        );
      },
      rootNodes () {
        return Object.keys(this.filteredNodes).map(i => this.filteredNodes[i]).
          filter(i => i?.parentId === null);
      }
    },
    watch: {
      searchText () {
        this.forceOpen = Boolean(this.searchText);
      }
    }
  };
</script>

<style>
  .tree-select-dropdown-tree__search {
    position: sticky;
    top: 0;
    z-index: 1;

    padding: 14px 16px 4px;

    background: var(--background-color);
  }

  .tree-select-dropdown-tree {
    overflow-y: auto;

    width: 282px;
    height: 100%;
  }

  .tree-select-dropdown-tree__node-skeleton {
    padding: 16px 20px 0;
  }
</style>
